import { Component, inject, OnInit } from '@angular/core';
import { LocationService } from './location.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
    selector: 'app-locations',
    standalone: true,
    imports: [FormsModule, RouterLink, CommonModule],
    templateUrl: './locations.component.html',
    styleUrl: './locations.component.scss',
})
export class LocationsComponent implements OnInit {
    locations: any[] = [];
    locationToDelete: any;
    currentPage = 1;
    pageSize = 10;
    totalLocations = 0;
    pageCount = 1;
    locationsLoading = false;
    locationInput$ = new Subject<string>();
    searchTerm = '';
    protected readonly Math = Math;

    private locationService = inject(LocationService);
    private modalService = inject(NgbModal);
    private toastr = inject(ToastrService);

    ngOnInit(): void {
        this.getLocations();
        this.locationInput$
            .pipe(debounceTime(300), distinctUntilChanged())
            .subscribe(() => {
                this.currentPage = 1;
                this.getLocations();
            });
    }

    getLocations(): void {
        this.locationsLoading = true;
        this.locationService.getLocations().subscribe({
            next: (res: any) => {
                // Backend doesn't support pagination/search yet based on documentation
                // But we will implement frontend filtering for now if needed, 
                // or just show the list as returned.
                const data = res?.data || res || [];
                this.locations = data;

                // Frontend filtering since API doesn't seem to support it yet based on the GET /locations specs
                if (this.searchTerm.trim()) {
                    this.locations = this.locations.filter(loc =>
                        loc.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
                        loc.type.toLowerCase().includes(this.searchTerm.toLowerCase())
                    );
                }

                this.totalLocations = this.locations.length;
                this.pageCount = Math.ceil(this.totalLocations / this.pageSize) || 1;

                // Manual pagination
                const start = (this.currentPage - 1) * this.pageSize;
                const end = start + this.pageSize;
                this.locations = this.locations.slice(start, end);

                this.locationsLoading = false;
            },
            error: (error: any) => {
                console.error('Error fetching locations:', error);
                this.toastr.error('Failed to fetch locations.');
                this.locationsLoading = false;
            },
        });
    }

    pageSizeChange() {
        this.currentPage = 1;
        this.getLocations();
    }

    onSearch() {
        this.currentPage = 1;
        this.locationInput$.next(this.searchTerm.trim());
    }

    goToPage(page: number): void {
        if (page < 1 || page > this.pageCount || page === this.currentPage) return;
        this.currentPage = page;
        this.getLocations();
    }

    getVisiblePages(): number[] {
        const pages: number[] = [];
        const start = Math.max(2, this.currentPage - 2);
        const end = Math.min(this.pageCount - 1, this.currentPage + 2);

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    }

    toggleStatus(location: any): void {
        const newStatus = !location.isActive;
        this.locationsLoading = true;

        this.locationService.updateLocation(location.id, { isActive: newStatus }).subscribe({
            next: () => {
                location.isActive = newStatus;
                this.toastr.success('Location status updated successfully!');
                this.locationsLoading = false;
            },
            error: (error: any) => {
                console.error('Error updating location status:', error);
                this.toastr.error('Failed to update location status.');
                this.locationsLoading = false;
            },
        });
    }

    openDeleteConfirmationModal(content: any, location: any) {
        this.locationToDelete = location;
        this.modalService.open(content, {
            ariaLabelledBy: 'confirmDeleteModalLabel',
        });
    }

    confirmDelete() {
        if (!this.locationToDelete?.id) return;

        this.locationService.deleteLocation(this.locationToDelete.id).subscribe({
            next: () => {
                this.toastr.success(`${this.locationToDelete.name} marked as inactive.`);
                this.getLocations();
                this.modalService.dismissAll();
            },
            error: (err) => {
                console.error('Error deleting location:', err);
                this.toastr.error('Failed to delete the location.');
            },
        });
    }

    closeModal() {
        this.modalService.dismissAll();
    }
}
